/**
 * Multi-Decision Accordion Component
 *
 * Displays multiple rule engine decisions for a single ASIN in a timeline format
 * - Most recent pending decision expanded by default
 * - Older decisions collapsed with summary
 * - Clear chronological order with agent avatars
 */

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AGENT_INFO,
  decisionHasActiveDeal,
  getAgentKey,
  getDealKey,
  getIrisPpcState,
  isDoneForYouDecision,
  isFionaDecision,
  isFionaPreviewDecision,
  isRossNotImplementedDecision,
  isRossRevertDecision,
} from "../../constants";
import FionaCard from "../AgentRecommendations/fiona/FionaCard";
import {
  isFollowUpDue,
  statusLabel,
} from "../AgentRecommendations/fiona/fionaRules";

import DaveMonitorCard, {
  DaveLabelChip,
  earlierUpdatesText,
} from "./DaveDeal/DaveMonitorCard";
import { getDaveCardView, isHiddenDaveCard } from "./DaveDeal/daveCardCopy";
import { collapseDaveDealCards } from "./DaveDeal/daveDealGroups";
import { FionaRowSaving, formatEffectiveDate } from "./FionaDecisionCard";
import IrisDecisionTreeModal from "./IrisDecisionTreeModal/IrisDecisionTreeModal";
import IrisPpcCard, { getIrisPpcDoneLines } from "./IrisPpcCard";
import ReginaDecisionCard from "./ReginaDecisionCard";
import RossDecisionCard from "./RossDecisionCard";
import RossNotImplementedCard from "./RossNotImplementedCard";
import RossRevertCard, {
  SELLER_CENTRAL_OVERRIDE_NOTE,
  buildRossRevertDoneMessage,
  getRossRevertDetailLines,
} from "./RossRevertCard";
import RuleEngineDecision from "./RuleEngineDecision";
import SamDecisionCard from "./SamDecisionCard";

const getAgentInfo = (agentName) => {
  const name = agentName?.toLowerCase() || "iris";
  return AGENT_INFO[name] || AGENT_INFO.iris;
};

// Scrolls the bottom of its content into view when the content height increases
const ScrollOnGrow = ({ children }) => {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const prevHeight = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > prevHeight.current && prevHeight.current > 0) {
          bottomRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
        prevHeight.current = newHeight;
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef}>
      {children}
      <div ref={bottomRef} />
    </div>
  );
};

const AUTOMATION_CHIP = {
  in_progress: { label: "Automating…", color: "primary" },
  succeeded: { label: "Automated ✓", color: "success" },
  all_attempts_failed: { label: "Manual Action Needed", color: "error" },
};

const formatTimeShort = (dateString) => {
  if (!dateString) return "";
  const diffMs = new Date() - new Date(dateString);
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "< 1h";
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
};

const MultiDecisionAccordion = ({
  decisions,
  asin,
  sku,
  store,
  marketplace,
  currentPrice,
  onRemoveDecision,
  onDealRejected,
  // (decision, change) when a Fiona card is answered or moves on, see
  // AgentRecommendations/fiona/FionaActions
  onFionaCardChanged,
  sx,
}) => {
  const navigate = useNavigate();

  // Sort decisions by created_at (most recent first)
  const sortedDecisions = [...decisions].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });

  // Split: succeeded Iris decisions and succeeded Ross price reverts go to
  // "Done for you", everything else stays in accordion
  // TODO: in_progress cards are disabled by now, if not used in a future just delete it
  // Original (shows in_progress cards):
  // const pendingDecisions = sortedDecisions.filter(
  //   (d) =>
  //     !(
  //       d.decision?.agent?.toLowerCase() === "iris" &&
  //       d.automated_execution_status === "succeeded"
  //     )
  // );
  // Interim Stage 1 rule: while an unresolved deal offer is live on this
  // ASIN, don't also show a separate Iris price recommendation for it — the
  // deal already covers that intent, and showing both invites the user to
  // action two conflicting price changes at once. The deal-bearing decision
  // itself is never suppressed by its own presence. Removed once Dave gets
  // his own decision row (Stage 3, AGS-725).
  const hasLiveDealOnAsin = sortedDecisions.some((d) =>
    decisionHasActiveDeal(d)
  );

  // One Dave card per deal, its older events listed inside it
  const pendingDecisions = collapseDaveDealCards(
    sortedDecisions.filter((d) => {
      // Dave's done cards are his own cards, not "Done for you" lines
      if (isDoneForYouDecision(d) && getAgentKey(d) !== "dave") return false;
      if (isRossNotImplementedDecision(d)) return false;
      if (isFionaPreviewDecision(d)) return false;
      // A Dave issue card with nothing left to show the seller
      if (isHiddenDaveCard(d)) return false;
      // Iris is still pausing the ads
      if (getIrisPpcState(d) === "pending") return false;
      if (
        d.decision?.agent?.toLowerCase() === "iris" &&
        d.automated_execution_status === "in_progress"
      ) {
        return false;
      }
      if (
        hasLiveDealOnAsin &&
        !decisionHasActiveDeal(d) &&
        d.decision?.agent?.toLowerCase() === "iris" &&
        (d.decision_type || "price") === "price"
      ) {
        return false;
      }
      return true;
    })
  );
  const doneDecisions = sortedDecisions.filter(
    (d) => isDoneForYouDecision(d) && getAgentKey(d) !== "dave"
  );
  // Nothing to act on or measure: Ross found the accepted change was never
  // applied, so it's an FYI note rather than an analysis card
  const fyiDecisions = sortedDecisions.filter(isRossNotImplementedDecision);
  // Fiona's cards priced on the next fee schedule, kept apart from today's
  const previewDecisions = sortedDecisions.filter(isFionaPreviewDecision);

  // Start with all panels collapsed
  const [expanded, setExpanded] = useState(false);
  const [doneExpanded, setDoneExpanded] = useState(true);
  const [fyiExpanded, setFyiExpanded] = useState(true);
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [openTreeExecutionId, setOpenTreeExecutionId] = useState(null);
  // Deal fields on `decision` don't change when the user rejects Dave's
  // offer locally — track it here so the row's lead avatar can drop back
  // to the sensing agent instead of staying stuck on Dave. Keyed by
  // getDealKey rather than execution_id, which isn't guaranteed unique
  // per decision (it can repeat across ASINs or come back empty).
  const [rejectedDealKeys, setRejectedDealKeys] = useState(() => new Set());
  const accordionRefs = useRef({});

  const setAccordionRef = useCallback(
    (panelId) => (el) => {
      accordionRefs.current[panelId] = el;
    },
    []
  );

  const handleDealRejected = (dealKey) => {
    setRejectedDealKeys((prev) => new Set(prev).add(dealKey));
    onDealRejected?.(dealKey);
  };

  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    if (isExpanded) {
      setTimeout(() => {
        accordionRefs.current[panel]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 300);
    }
  };

  // Handle removing a decision (or group of decisions) from the accordion
  const handleRemoveDecision = (executionIdOrIds) => {
    setExpanded(false);
    if (onRemoveDecision) {
      if (Array.isArray(executionIdOrIds)) {
        executionIdOrIds.forEach((id) => onRemoveDecision(id));
      } else {
        onRemoveDecision(executionIdOrIds);
      }
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const buildDoneMessage = (decision) => {
    const action = decision.decision?.action || "";
    const verb = action.toLowerCase().includes("increase price")
      ? "increased"
      : action.toLowerCase().includes("reduce price")
      ? "reduced"
      : "updated";
    const from = decision.selling_price ? `$${decision.selling_price}` : null;
    const to = decision.decision?.target_price
      ? `$${decision.decision.target_price}`
      : null;
    if (from && to) {
      return `Iris ${verb} the price from ${from} → ${to}`;
    }
    return `Iris ${verb} the price`;
  };

  return (
    <Box
      sx={{
        p: 2,
        minWidth: 420,
        maxWidth: 480,
        backgroundColor: "#fff",
        ...sx,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Agent Decisions (
          {pendingDecisions.length +
            previewDecisions.length +
            fyiDecisions.length +
            doneDecisions.length}
          )
        </Typography>
        <Tooltip title="View in Store Analysis" arrow>
          <Chip
            label={asin}
            size="small"
            clickable
            onClick={() =>
              navigate(
                `/store-products-analysis?store=${store}&asin=${asin}&marketplace=${
                  marketplace || "us"
                }`
              )
            }
            sx={{
              fontFamily: "monospace",
              fontSize: "0.7rem",
              height: 20,
              color: "#999",
              backgroundColor: "#f5f5f5",
              "&:hover": { backgroundColor: "#e3f2fd", color: "#1976d2" },
            }}
          />
        </Tooltip>
      </Box>

      {/* Pending decisions — timeline accordion */}
      {pendingDecisions.length > 0 && (
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              left: 28,
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: "#e0e0e0",
              zIndex: 0,
            }}
          />

          {pendingDecisions.map((item, index) => {
            const isSamGroup = item._isSamGroup;
            const decision = isSamGroup ? item.decisions[0] : item;
            const agent = getAgentInfo(
              isSamGroup ? "sam" : decision.decision?.agent || decision.agent
            );
            // Dave is the one acting once a deal is live — he leads the row
            // (avatar + name), with the sensing agent demoted to a small
            // badge, instead of the other way around.
            const hasDeal =
              !isSamGroup &&
              decisionHasActiveDeal(decision) &&
              !rejectedDealKeys.has(getDealKey(decision));
            const panelId = `panel-${index}`;
            const agentName = decision.decision?.agent?.toLowerCase();
            const isRossAgent =
              agentName === "ross" || decision.version?.includes("ross");
            const isSamAgent =
              isSamGroup ||
              decision.decision_type === "sam" ||
              agentName === "sam";
            const isReginaAgent =
              agentName === "regina" || decision.version?.includes("regina");
            const isRossRevert = !isSamGroup && isRossRevertDecision(decision);
            const isFionaAgent = !isSamGroup && isFionaDecision(decision);
            const irisPpcState = isSamGroup ? null : getIrisPpcState(decision);
            // Dave's own deal cards: an issue or reminder, what he did on a
            // deal (registered, changed its price, cancelled it), or a
            // registration he couldn't complete
            const daveCardView =
              !isSamGroup && getAgentKey(decision) === "dave"
                ? getDaveCardView(decision, { marketplace })
                : null;
            // Same dual-agent treatment for Ross price reverts: Ross only
            // triggers them, Iris executes them, so Iris leads with Ross as
            // the badge.
            const leadAgent = hasDeal
              ? AGENT_INFO.dave
              : isRossRevert
              ? AGENT_INFO.iris
              : null;

            // A Fiona run's today and preview cards can share execution_id
            const itemKey = isSamGroup
              ? `sam-group-${item.keyword}`
              : daveCardView
              ? `dave-card-${decision.card_id ?? decision.execution_id}`
              : isFionaAgent
              ? `${decision.execution_id}-${decision.decision?.card_group}`
              : decision.execution_id || index;

            return (
              <Accordion
                ref={setAccordionRef(panelId)}
                key={itemKey}
                expanded={expanded === panelId}
                onChange={handleChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  "&:before": { display: "none" },
                  backgroundColor: "transparent",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 60,
                    "&.Mui-expanded": {
                      minHeight: 60,
                    },
                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                      alignItems: "center",
                    },
                  }}
                >
                  {/* Timeline dot with agent avatar — the acting agent leads
                      (Dave on live deals, Iris on Ross reverts) */}
                  {leadAgent ? (
                    <Box
                      sx={{
                        position: "relative",
                        width: 48,
                        height: 48,
                        flex: "none",
                        mr: 2,
                      }}
                    >
                      <Avatar
                        src={leadAgent.avatar}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `3px solid ${leadAgent.color}`,
                          backgroundColor: "#fff",
                        }}
                      />
                      <Avatar
                        src={agent.avatar}
                        sx={{
                          position: "absolute",
                          right: -7,
                          bottom: -5,
                          width: 24,
                          height: 24,
                          border: `2px solid ${agent.color}`,
                          backgroundColor: "#fff",
                        }}
                      />
                    </Box>
                  ) : (
                    <Avatar
                      src={agent.avatar}
                      sx={{
                        width: 48,
                        height: 48,
                        border: `3px solid ${agent.color}`,
                        backgroundColor: "#fff",
                        mr: 2,
                        zIndex: 1,
                      }}
                    />
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: (leadAgent || agent).color,
                        }}
                      >
                        {(leadAgent || agent).name}
                      </Typography>
                      {!isSamAgent &&
                        (!isRossAgent || isRossRevert) &&
                        !isReginaAgent &&
                        !isFionaAgent &&
                        !daveCardView &&
                        decision.automated_execution_status &&
                        AUTOMATION_CHIP[
                          decision.automated_execution_status
                        ] && (
                          <Chip
                            label={
                              AUTOMATION_CHIP[
                                decision.automated_execution_status
                              ].label
                            }
                            color={
                              AUTOMATION_CHIP[
                                decision.automated_execution_status
                              ].color
                            }
                            size="small"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      {daveCardView && (
                        <DaveLabelChip label={daveCardView.label} />
                      )}
                      {irisPpcState === "manual" && (
                        <Chip
                          label={AUTOMATION_CHIP.all_attempts_failed.label}
                          color={AUTOMATION_CHIP.all_attempts_failed.color}
                          size="small"
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      )}
                      {isFionaAgent &&
                        decision.fiona_card?.status &&
                        decision.fiona_card.status !== "open" && (
                          <Chip
                            label={
                              isFollowUpDue(decision.fiona_card)
                                ? "Check-in due"
                                : statusLabel(decision.fiona_card.status)
                            }
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              ...(isFollowUpDue(decision.fiona_card)
                                ? {
                                    backgroundColor: "#fff3e0",
                                    color: "#e65100",
                                  }
                                : {
                                    backgroundColor: "#e8eaf6",
                                    color: AGENT_INFO.fiona.color,
                                  }),
                            }}
                          />
                        )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#333",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {isSamGroup
                        ? `"${item.keyword}"`
                        : hasDeal
                        ? "Use Best Deal"
                        : isRossRevert
                        ? "Revert Price"
                        : daveCardView
                        ? daveCardView.title
                        : decision.decision?.action || "Decision"}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#999",
                        display: "block",
                      }}
                    >
                      {/* A Dave deal card dates from its newest event */}
                      {formatRelativeTime(
                        [item, ...(decision.daveEarlier || [])]
                          .map((d) => d.created_at)
                          .filter(Boolean)
                          .sort()
                          .pop()
                      )}
                      {daveCardView && decision.daveEarlier?.length > 0
                        ? ` · ${earlierUpdatesText(
                            decision.daveEarlier.length
                          )}`
                        : ""}
                    </Typography>
                  </Box>
                  {isFionaAgent && (
                    <FionaRowSaving decision={decision.decision} />
                  )}
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    ml: 7,
                    p: 0,
                    pb: 2,
                  }}
                >
                  <ScrollOnGrow>
                    {isSamAgent ? (
                      <SamDecisionCard
                        asin={asin}
                        store={store}
                        marketplace={marketplace}
                        samDecisions={isSamGroup ? item.decisions : [decision]}
                        onRemove={() =>
                          handleRemoveDecision(
                            isSamGroup
                              ? item.decisions.map((d) => d.execution_id)
                              : decision.execution_id
                          )
                        }
                      />
                    ) : isReginaAgent ? (
                      <ReginaDecisionCard
                        asin={asin}
                        sku={sku}
                        store={store}
                        marketplace={marketplace}
                        executionId={decision.execution_id}
                        reginaData={decision}
                        onRemove={() =>
                          handleRemoveDecision(decision.execution_id)
                        }
                        onDealRejected={() =>
                          handleDealRejected(getDealKey(decision))
                        }
                      />
                    ) : daveCardView ? (
                      <DaveMonitorCard
                        daveData={decision}
                        marketplace={marketplace}
                      />
                    ) : isFionaAgent ? (
                      <FionaCard
                        decision={decision}
                        store={store}
                        marketplace={marketplace}
                        onCardChanged={onFionaCardChanged}
                        withStatusPills={false}
                      />
                    ) : irisPpcState ? (
                      <IrisPpcCard
                        decision={decision}
                        asin={asin}
                        sku={sku}
                        store={store}
                        marketplace={marketplace}
                        onRemove={() =>
                          handleRemoveDecision(decision.execution_id)
                        }
                      />
                    ) : isRossRevert ? (
                      <RossRevertCard
                        rossData={decision}
                        asin={asin}
                        sku={sku}
                        store={store}
                        marketplace={marketplace}
                        currentPrice={currentPrice}
                        onRemove={() =>
                          handleRemoveDecision(decision.execution_id)
                        }
                      />
                    ) : isRossAgent ? (
                      <RossDecisionCard
                        asin={asin}
                        sku={sku}
                        store={store}
                        marketplace={marketplace}
                        executionId={decision.execution_id}
                        rossData={decision}
                        onRemove={() =>
                          handleRemoveDecision(decision.execution_id)
                        }
                      />
                    ) : (
                      <RuleEngineDecision
                        asin={asin}
                        sku={sku}
                        executionId={decision.execution_id}
                        targetPrice={decision.decision?.target_price || null}
                        sellingPrice={decision.selling_price || null}
                        action={decision.decision?.action}
                        agentName={decision.decision?.agent}
                        currentPrice={currentPrice}
                        store={store}
                        marketplace={marketplace}
                        decisionType={decision.decision_type || "price"}
                        dealAvailable={decision.deal_available}
                        deal={decision.deal}
                        automatedExecutionStatus={
                          decision.automated_execution_status
                        }
                        onRemove={() =>
                          handleRemoveDecision(decision.execution_id)
                        }
                        onDealRejected={() =>
                          handleDealRejected(getDealKey(decision))
                        }
                      />
                    )}
                  </ScrollOnGrow>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Fiona's next-fee-schedule section */}
      {previewDecisions.length > 0 && (
        <Box sx={{ mt: pendingDecisions.length > 0 ? 2 : 0 }}>
          <Box
            onClick={() => setPreviewExpanded((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              mb: previewExpanded ? 1.5 : 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#aaa",
                letterSpacing: "0.08em",
                whiteSpace: "nowrap",
              }}
            >
              NEXT FEE SCHEDULE
            </Typography>
            <Divider sx={{ flex: 1 }} />
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "#aaa",
                transform: previewExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </Box>

          {previewExpanded && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {previewDecisions.map((decision) => (
                <Box
                  key={`${decision.execution_id}-${decision.decision?.card_group}`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Avatar
                      src={AGENT_INFO.fiona.avatar}
                      sx={{
                        width: 24,
                        height: 24,
                        border: `2px solid ${AGENT_INFO.fiona.color}`,
                        backgroundColor: "#fff",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#333" }}
                    >
                      {decision.decision?.action || "Lower FBA Fee"}
                    </Typography>
                    {decision.decision?.effective_date && (
                      <Chip
                        label={`From ${formatEffectiveDate(
                          decision.decision.effective_date
                        )}`}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    )}
                    <Box sx={{ ml: "auto" }}>
                      <FionaRowSaving decision={decision.decision} />
                    </Box>
                  </Box>
                  <FionaCard
                    decision={decision}
                    store={store}
                    marketplace={marketplace}
                    onCardChanged={onFionaCardChanged}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* For your info section */}
      {fyiDecisions.length > 0 && (
        <Box
          sx={{
            mt: pendingDecisions.length + previewDecisions.length > 0 ? 2 : 0,
          }}
        >
          <Box
            onClick={() => setFyiExpanded((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              mb: fyiExpanded ? 1.5 : 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#aaa",
                letterSpacing: "0.08em",
                whiteSpace: "nowrap",
              }}
            >
              FOR YOUR INFO
            </Typography>
            <Divider sx={{ flex: 1 }} />
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "#aaa",
                transform: fyiExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </Box>

          {fyiExpanded && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {fyiDecisions.map((decision) => (
                <RossNotImplementedCard
                  key={decision.execution_id}
                  asin={asin}
                  sku={sku}
                  store={store}
                  marketplace={marketplace}
                  executionId={decision.execution_id}
                  rossData={decision}
                  onRemove={() => handleRemoveDecision(decision.execution_id)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Done for you section */}
      {doneDecisions.length > 0 && (
        <Box
          sx={{
            mt:
              pendingDecisions.length +
                previewDecisions.length +
                fyiDecisions.length >
              0
                ? 2
                : 0,
          }}
        >
          {/* Section header */}
          <Box
            onClick={() => setDoneExpanded((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              mb: doneExpanded ? 1.5 : 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#aaa",
                letterSpacing: "0.08em",
                whiteSpace: "nowrap",
              }}
            >
              DONE FOR YOU
            </Typography>
            <Divider sx={{ flex: 1 }} />
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "#aaa",
                transform: doneExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </Box>

          {/* Done items list */}
          {doneExpanded && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {doneDecisions.map((decision) => {
                const isRossRevert = isRossRevertDecision(decision);
                const rossDetailLines = isRossRevert
                  ? getRossRevertDetailLines(decision)
                  : [];
                const ppcLines = getIrisPpcDoneLines(decision);
                return (
                  <Box
                    key={decision.execution_id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "#bbb", whiteSpace: "nowrap" }}
                    >
                      {decision.created_at
                        ? new Date(decision.created_at).toLocaleDateString(
                            "en-US",
                            { month: "short", day: "numeric" }
                          )
                        : ""}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#4caf50",
                        fontWeight: 700,
                        fontSize: "1rem",
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </Typography>
                    {isRossRevert || ppcLines ? (
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: "#333" }}>
                          {ppcLines
                            ? ppcLines.message
                            : buildRossRevertDoneMessage(decision)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#aaa", fontStyle: "italic" }}
                        >
                          {ppcLines
                            ? ppcLines.note
                            : SELLER_CENTRAL_OVERRIDE_NOTE}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "#333", flex: 1 }}
                      >
                        {buildDoneMessage(decision)}
                      </Typography>
                    )}
                    {isRossRevert ? (
                      // Ross reverts have no Iris decision tree — explain inline
                      rossDetailLines.length > 0 && (
                        <Tooltip
                          arrow
                          title={rossDetailLines.map((line) => (
                            <div key={line}>{line}</div>
                          ))}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#999",
                              cursor: "help",
                              textDecoration: "underline",
                              whiteSpace: "nowrap",
                              "&:hover": { color: "#666" },
                            }}
                          >
                            Why?
                          </Typography>
                        </Tooltip>
                      )
                    ) : (
                      <Typography
                        variant="caption"
                        onClick={() =>
                          setOpenTreeExecutionId(decision.execution_id)
                        }
                        sx={{
                          color: "#999",
                          cursor: "pointer",
                          textDecoration: "underline",
                          whiteSpace: "nowrap",
                          "&:hover": { color: "#666" },
                        }}
                      >
                        Why?
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: "#bbb", whiteSpace: "nowrap", minWidth: 24 }}
                    >
                      {formatTimeShort(decision.created_at)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Decision tree modal for "Done for you" Why? links */}
      {openTreeExecutionId && (
        <IrisDecisionTreeModal
          open={Boolean(openTreeExecutionId)}
          onClose={() => setOpenTreeExecutionId(null)}
          asin={asin}
          executionId={openTreeExecutionId}
          store={store}
          marketplace={marketplace}
        />
      )}
    </Box>
  );
};

export default MultiDecisionAccordion;
